import { AlertCircle, Code01 } from "@untitledui/icons";
import { useId, useState } from "react";
import {
	DialogTrigger as AriaDialogTrigger,
	Heading as AriaHeading,
} from "react-aria-components";
import { toast } from "sonner";
import {
	Dialog,
	Modal,
	ModalOverlay,
} from "@/components/application/modals/modal";
import { Avatar } from "@/components/base/avatar/avatar";
import { Button } from "@/components/base/buttons/button";
import { Toast } from "@/components/Molecules/Toast/Toast";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useDeleteWorkflowRule } from "@/services/workflow";

export interface DeleteWorkflowRuleModalProps {
	isOpen: boolean;
	setIsOpen: (open: boolean) => void;
	handleClose: () => void;
	onDeleteSuccess: () => void;
	ruleId: string;
	ruleName: string;
	/** Current rule status — used to guard against deleting active rules. */
	ruleStatus: string;
	organizationId: string;
}

export function DeleteWorkflowRuleModal({
	isOpen,
	setIsOpen,
	handleClose,
	onDeleteSuccess,
	ruleId,
	ruleName,
	ruleStatus,
	organizationId,
}: DeleteWorkflowRuleModalProps) {
	const deleteMutation = useDeleteWorkflowRule();
	const toastId = useId();
	const [error, setError] = useState<string | null>(null);
	const { t } = useAppTranslation();

	const onClose = () => {
		handleClose();
		setError(null);
		deleteMutation.reset();
	};

	const handleDeleteWorkflowRule = () => {
		if (ruleStatus === "active") {
			toast.custom(
				(toastIdParam) => (
					<Toast
						id={toastId}
						color="error"
						addonText="Error"
						message={t("admin.workflowRules.delete.activeError")}
						showCloseButton
						onClose={() => toast.dismiss(toastIdParam)}
					/>
				),
				{ duration: 5000 },
			);
			return;
		}

		deleteMutation.mutate(
			{ ruleId, organizationId },
			{
				onSuccess: () => {
					toast.custom(
						(toastIdParam) => (
							<Toast
								id={toastId}
								color="success"
								addonText="Success"
								message={t("admin.workflowRules.delete.success")}
								showCloseButton
								onClose={() => toast.dismiss(toastIdParam)}
							/>
						),
						{ duration: 5000 },
					);
					onDeleteSuccess();
					handleClose();
				},
				onError: (err: unknown) => {
					const defaultError =
						(err as Error)?.message ?? t("common.errors.unknownError");
					const fallback = t("admin.workflowRules.delete.errorTitle");
					const messages = (
						err as { response?: { data?: { errors?: string[] } } }
					)?.response?.data?.errors ?? [defaultError];
					const message = messages[0] ?? fallback;
					setError(message);
					toast.custom(
						(toastIdParam) => (
							<Toast
								id={toastId}
								color="error"
								addonText="Error"
								message={message}
								showCloseButton
								onClose={() => toast.dismiss(toastIdParam)}
							/>
						),
						{ duration: 5000 },
					);
				},
			},
		);
	};

	return (
		<AriaDialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
			<ModalOverlay isDismissable={false}>
				<Modal>
					<Dialog>
						<div className="relative w-full overflow-hidden rounded-2xl bg-primary shadow-xl sm:max-w-[480px]">
							<div className="flex flex-col gap-4 px-4 pt-5 sm:px-6 sm:pt-6">
								<div className="relative mx-auto w-max">
									{deleteMutation.error ? (
										<Avatar
											size="2xl"
											placeholderIcon={({ className }) => (
												<AlertCircle
													className={className ?? ""}
													size={32}
													style={{ stroke: "var(--Error-600, #D92D20)" }}
												/>
											)}
											className="bg-error-100! outline-none!"
										/>
									) : (
										<Avatar
											size="2xl"
											blackIcon
											placeholderIcon={({ className }) => (
												<Code01 className={className ?? ""} size={32} />
											)}
											className="bg-error-50! ring-1 ring-error-200"
										/>
									)}
								</div>
								<div className="z-10 flex flex-col gap-0.5 text-center">
									<AriaHeading
										slot="title"
										className="text-md font-semibold text-primary"
									>
										{deleteMutation.error
											? t("admin.workflowRules.delete.errorTitle")
											: t("admin.workflowRules.delete.title")}
									</AriaHeading>
									<p className="text-sm text-tertiary">
										{deleteMutation.error
											? error
											: t("admin.workflowRules.delete.description")}
									</p>
									<div className="h-5 w-full" />
									<div className="rounded-lg bg-gray-50 p-4 text-left ring-1 ring-gray-200">
										<p className="text-sm text-tertiary">
											{t("admin.workflowRules.delete.referenceLabel")}
										</p>
										<p className="text-md mt-1 font-semibold text-primary">
											{ruleName}
										</p>
									</div>
								</div>
							</div>
							<div className="pt-6 sm:pt-8">
								<div className="z-10 flex flex-1 flex-col-reverse gap-3 border-t border-secondary p-4 *:grow sm:grid sm:grid-cols-2 sm:p-6">
									<Button
										color="secondary"
										size="lg"
										onClick={onClose}
										isDisabled={deleteMutation.isPending}
									>
										{t("common.actions.cancel")}
									</Button>
									<Button
										color="primary-destructive"
										size="lg"
										aria-label={t("admin.workflowRules.delete.deleteAriaLabel")}
										onClick={handleDeleteWorkflowRule}
										showTextWhileLoading
										isLoading={deleteMutation.isPending}
										isDisabled={deleteMutation.isPending}
									>
										{deleteMutation.isPending
											? `${t("admin.workflowRules.delete.deleting")}...`
											: t("admin.workflowRules.delete.button")}
									</Button>
								</div>
							</div>
						</div>
					</Dialog>
				</Modal>
			</ModalOverlay>
		</AriaDialogTrigger>
	);
}
